import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SEO from '../../components/SEO';
import PublicHeader from '../../components/PublicHeader';
import PublicFooter from '../../components/PublicFooter';
import './PublicPages.css';

export default function HowItWorksPage() {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="public-page">
      <SEO
        title="How Hangrily Works — Swipe to Decide Where to Eat in 4 Easy Steps"
        description="Pick your preferences, swipe through nearby restaurants with real photos & ratings, and match with your partner or group in under 2 minutes. Solo, couple & group modes. Smarter than random pickers. Free to try."
        path="/how-it-works"
        jsonLd={[
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://hangrily.app' },
              { '@type': 'ListItem', position: 2, name: 'How It Works', item: 'https://hangrily.app/how-it-works' }
            ]
          },
          {
            '@context': 'https://schema.org',
            '@type': 'HowTo',
            name: 'How to Use Hangrily to Decide Where to Eat',
            description: 'End restaurant indecision in 3 simple steps with Hangrily\'s swipe-based restaurant picker.',
            totalTime: 'PT2M',
            tool: { '@type': 'HowToTool', name: 'Hangrily Web App or Mobile App' },
            step: [
              { '@type': 'HowToStep', position: 1, name: 'Set Your Preferences', text: 'Tell us what you are in the mood for. Choose from 12+ cuisine types, set your price range ($-$$$$), pick your preferred distance, and select a mood (spicy, comfort food, healthy, etc.).' },
              { '@type': 'HowToStep', position: 2, name: 'Swipe on Restaurants', text: 'We show you restaurant cards with photos, ratings, cuisine type, price level, and distance. Swipe through them Tinder-style: Dislike to eliminate, Like to move forward, Love for priority scoring.' },
              { '@type': 'HowToStep', position: 3, name: 'Get Your Perfect Match', text: 'Our smart algorithm processes everyone\'s swipes through up to 3 rounds, narrowing down options until we find the restaurant everyone agrees on.' }
            ]
          },
          {
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: [
              {
                '@type': 'Question',
                name: 'How long does a session take?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'Most sessions complete in 2-5 minutes. Solo mode is fastest (1-2 minutes), while group sessions may take 3-5 minutes depending on how quickly everyone swipes.'
                }
              },
              {
                '@type': 'Question',
                name: 'What if we don\'t match on anything?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'Our algorithm is designed to find common ground. If you are very picky, we will show runner-up options. You can also restart with broader preferences.'
                }
              },
              {
                '@type': 'Question',
                name: 'Can I use this without creating an account?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'Yes! You get 3 free sessions per week without an account. Create a free account for unlimited sessions, saved preferences, partner linking, and history tracking.'
                }
              },
              {
                '@type': 'Question',
                name: 'Where does restaurant data come from?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'We pull real-time restaurant data from verified sources, including ratings, photos, hours, and locations. All information is kept current and accurate.'
                }
              }
            ]
          }
        ]}
      />
      <PublicHeader />

      <main className="page-content">
        <section className="page-hero">
          <h1>How Hangrily Works</h1>
          <p className="hero-subtitle">
            End restaurant indecision in 3 simple steps. No more arguments, no more wasted time.
          </p>
        </section>

        <section className="content-section">
          <div className="process-flow">
            <div className="process-step">
              <div className="step-visual">
                <div className="step-badge">Step 1</div>
                <div className="visual-icon">🎯</div>
              </div>
              <div className="step-details">
                <h2>Set Your Preferences</h2>
                <p>
                  Tell us what you're in the mood for. Choose from 12+ cuisine types, set your price range 
                  ($-$$$$), pick your preferred distance, and select a mood (spicy, comfort food, healthy, etc.).
                </p>
                <ul className="detail-list">
                  <li><strong>Cuisine Types:</strong> Mexican, Italian, Chinese, Japanese, Thai, Indian, American, BBQ, Pizza, Burgers, Seafood, Vietnamese, and more</li>
                  <li><strong>Price Ranges:</strong> Budget-friendly ($) to Fine Dining ($$$$)</li>
                  <li><strong>Distance:</strong> Within 1, 3, 5, or 10 miles</li>
                  <li><strong>Mood Options:</strong> Spicy 🌶️, Comfort 🍔, Healthy 🥗, Quick ⚡, Fancy ✨, Adventure 🌍</li>
                </ul>
              </div>
            </div>

            <div className="process-arrow">↓</div>

            <div className="process-step">
              <div className="step-visual">
                <div className="step-badge">Step 2</div>
                <div className="visual-icon">👆</div>
              </div>
              <div className="step-details">
                <h2>Swipe on Restaurants</h2>
                <p>
                  We'll show you restaurant cards with photos, ratings, cuisine type, price level, and distance. 
                  Swipe through them Tinder-style with three options:
                </p>
                <div className="swipe-options">
                  <div className="swipe-option">
                    <span className="swipe-icon dislike">👎</span>
                    <div>
                      <strong>Dislike</strong>
                      <p>Not interested - this restaurant gets eliminated</p>
                    </div>
                  </div>
                  <div className="swipe-option">
                    <span className="swipe-icon like">👍</span>
                    <div>
                      <strong>Like</strong>
                      <p>Sounds good - this restaurant moves forward</p>
                    </div>
                  </div>
                  <div className="swipe-option">
                    <span className="swipe-icon love">❤️</span>
                    <div>
                      <strong>Love</strong>
                      <p>Perfect choice - this gets priority scoring</p>
                    </div>
                  </div>
                </div>
                <div className="info-box">
                  <strong>In Couple/Group Mode:</strong> Everyone swipes simultaneously on their own device. 
                  You won't see what others picked until the round is complete.
                </div>
              </div>
            </div>

            <div className="process-arrow">↓</div>

            <div className="process-step">
              <div className="step-visual">
                <div className="step-badge">Step 3</div>
                <div className="visual-icon">🎉</div>
              </div>
              <div className="step-details">
                <h2>Get Your Perfect Match</h2>
                <p>
                  Our smart algorithm processes everyone's swipes through up to 3 rounds, narrowing down 
                  options until we find the restaurant everyone agrees on.
                </p>
                <div className="algorithm-explanation">
                  <h3>How Our Algorithm Works:</h3>
                  <ul className="detail-list">
                    <li><strong>Round 1:</strong> Start with 15 restaurants based on your preferences</li>
                    <li><strong>Round 2:</strong> Narrow down to top 8 based on collective likes/loves</li>
                    <li><strong>Round 3:</strong> Final 4 restaurants - highest scoring wins</li>
                    <li><strong>Scoring:</strong> Dislike = 0 points, Like = 1 point, Love = 2 points</li>
                    <li><strong>Early End:</strong> If only one restaurant remains with overwhelming support, we end early</li>
                  </ul>
                </div>
                <div className="result-preview">
                  <h3>Your Result Includes:</h3>
                  <ul className="detail-list">
                    <li>🏆 Winner restaurant with full details</li>
                    <li>📍 Address and directions to the restaurant</li>
                    <li>⭐ Rating and price information</li>
                    <li>🥈 Runner-up options (in case winner is closed/full)</li>
                    <li>📊 Session stats (rounds completed, participants, time taken)</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="modes-detail-section">
          <h2 className="section-title">Choose Your Mode</h2>
          
          <div className="mode-detail-card">
            <div className="mode-header">
              <span className="mode-icon-large">🍽️</span>
              <h3>Solo Mode</h3>
            </div>
            <p>
              Perfect for when you're eating alone but can't decide what you want. Hangrily acts as your 
              personal decision assistant, helping you narrow down options when decision fatigue hits.
            </p>
            <h4>How Solo Mode Works:</h4>
            <ol className="numbered-list">
              <li>Set your preferences (cuisine, price, distance, mood)</li>
              <li>Swipe through 15 restaurant options</li>
              <li>Get your top match in 3 rounds or less</li>
              <li>Save favorites for future sessions</li>
            </ol>
            <div className="mode-tip">
              <strong>Pro Tip:</strong> Use the "Love" swipe on restaurants you're really excited about 
              to ensure they make it to the final round.
            </div>
          </div>

          <div className="mode-detail-card featured">
            <div className="mode-header">
              <span className="mode-icon-large">💑</span>
              <h3>Couple Mode</h3>
              <span className="popular-badge">Most Popular</span>
            </div>
            <p>
              The ultimate solution for couples who waste time arguing about where to eat. Link your 
              partner's account once, then start sessions together anytime.
            </p>
            <h4>How Couple Mode Works:</h4>
            <ol className="numbered-list">
              <li>Link your partner's account (one-time setup via email)</li>
              <li>Either partner can start a session - the other gets notified</li>
              <li>Both swipe on the same restaurants simultaneously</li>
              <li>Only restaurants you BOTH like move forward</li>
              <li>Get a match you both agree on - no arguments!</li>
            </ol>
            <h4>Special Couple Features:</h4>
            <ul className="detail-list">
              <li><strong>Hunger Nudges:</strong> Send a playful notification when you're ready to eat</li>
              <li><strong>Shared History:</strong> See all your past decisions together</li>
              <li><strong>Quick Sessions:</strong> Partner auto-joins when you create a session</li>
              <li><strong>Fair Matching:</strong> Algorithm ensures both preferences are weighted equally</li>
            </ul>
          </div>

          <div className="mode-detail-card">
            <div className="mode-header">
              <span className="mode-icon-large">👥</span>
              <h3>Group Mode</h3>
            </div>
            <p>
              Planning dinner with friends, family, or coworkers? Group mode handles up to 10 participants 
              and finds a restaurant everyone can agree on.
            </p>
            <h4>How Group Mode Works:</h4>
            <ol className="numbered-list">
              <li>Create a session and get a 6-digit code (e.g., ABC123)</li>
              <li>Share the code with your group via text, email, or social media</li>
              <li>Everyone joins using the code and sets their preferences</li>
              <li>All participants swipe simultaneously</li>
              <li>Our algorithm finds the best match based on group consensus</li>
            </ol>
            <h4>Group Features:</h4>
            <ul className="detail-list">
              <li><strong>Democratic Voting:</strong> Every vote counts equally</li>
              <li><strong>Majority Rules:</strong> Restaurant needs 50%+ approval to advance</li>
              <li><strong>Real-time Updates:</strong> See when others finish swiping</li>
              <li><strong>Session Codes:</strong> Easy to share and join</li>
            </ul>
          </div>
        </section>

        <section className="faq-preview">
          <h2 className="section-title">Common Questions</h2>
          <div className="faq-grid">
            <div className="faq-item">
              <h3>How long does a session take?</h3>
              <p>
                Most sessions complete in 2-5 minutes. Solo mode is fastest (1-2 minutes), while 
                group sessions may take 3-5 minutes depending on how quickly everyone swipes.
              </p>
            </div>
            <div className="faq-item">
              <h3>What if we don't match on anything?</h3>
              <p>
                Our algorithm is designed to find common ground. If you're very picky, we'll show 
                runner-up options. You can also restart with broader preferences.
              </p>
            </div>
            <div className="faq-item">
              <h3>Can I use this without creating an account?</h3>
              <p>
                Yes! You get 3 free sessions per week without an account. Create a free account for 
                unlimited sessions, saved preferences, partner linking, and history tracking.
              </p>
            </div>
            <div className="faq-item">
              <h3>Where does restaurant data come from?</h3>
              <p>
                We pull real-time restaurant data from verified sources, including ratings, photos, 
                hours, and locations. All information is kept current and accurate.
              </p>
            </div>
          </div>
          <button className="btn btn-secondary btn-lg" onClick={() => navigate('/faq')}>
            See All FAQs
          </button>
        </section>

        <section className="cta-banner">
          <h2>Ready to Stop Arguing?</h2>
          <p>Join thousands of users who've ended restaurant indecision forever.</p>
          <button className="btn btn-primary btn-lg" onClick={() => navigate('/try')}>
            Try It Free — No Account Needed
          </button>
        </section>
      </main>

      <PublicFooter />
    </div>
  );
}
